import { ReactNode, useContext, useEffect, useRef, useState } from 'react'
import ErrorCard from '@/components/error/ErrorCard'
import FullPageLoader from '@/components/loaders/FullPageLoader'
import { AuthContext } from '@/context/auth-context'
import { postUserBootstrap } from '@/account/fetch/post-user-bootstrap'
import { getCurrentUserSampleMatterId } from '@/matter/utils/get-current-user-sample-matter-id'
import { Userpilot } from 'userpilot'
import { ApiError } from '@/util/errors/api-error'

type AccountBootstrapGuardProps = {
  children: ReactNode
}

type BootstrapStatus = 'idle' | 'pending' | 'success' | 'error'

type BootstrapState = {
  status: BootstrapStatus
  uid: string | null
}

export default function AccountBootstrapGuard({ children }: AccountBootstrapGuardProps) {
  const { user, userAccountData, refreshUserAccountData } = useContext(AuthContext)
  const [bootstrapState, setBootstrapState] = useState<BootstrapState>({ status: 'idle', uid: null })
  const [retryCount, setRetryCount] = useState(0)
  // The provider recreates this function on every render; reading it through a ref keeps it from
  // restarting bootstrap.
  const refreshUserAccountDataRef = useRef(refreshUserAccountData)
  refreshUserAccountDataRef.current = refreshUserAccountData

  const uid = user?.uid ?? null
  const hasUserAccountData = userAccountData != null
  const isOrgAccount = userAccountData?.isOrgAccount === true
  const surveyCompleted = userAccountData?.surveyCompleted === true
  const shouldSkipBootstrap = user?.isAnonymous === true || isOrgAccount

  // Reset bootstrap state whenever the Firebase identity changes.
  useEffect(() => {
    setBootstrapState({ status: 'idle', uid })
    setRetryCount(0)
  }, [uid])

  // Run the account bootstrap request for eligible users.
  useEffect(() => {
    if (!uid || shouldSkipBootstrap || !hasUserAccountData) return

    let cancelled = false

    setBootstrapState({ status: 'pending', uid })

    postUserBootstrap()
      .then(async () => {
        if (cancelled) return

        try {
          const sampleMatterId = await getCurrentUserSampleMatterId()
          if (cancelled) return

          if (sampleMatterId) {
            Userpilot.identify(uid, { sampleMatterId })
            Userpilot.reload()
          } else {
            console.warn('Userpilot sample matter id missing after account bootstrap')
          }
        } catch (error) {
          if (cancelled) return

          console.error(`Userpilot sample matter id lookup failed after account bootstrap for uid ${uid}:`, error)
        }

        if (!cancelled) setBootstrapState({ status: 'success', uid })
      })
      .catch(async (error) => {
        if (cancelled) return

        if (surveyCompleted) {
          setBootstrapState({ status: 'success', uid })
          return
        }

        // The API reads organization membership from the token it receives, so a 409 means that
        // token names an organization the cached account data predates. Refreshing it marks the
        // account as an organization member, which skips bootstrap instead of stopping here.
        if (error instanceof ApiError && error.status === 409) {
          const refreshedAccountData = await refreshUserAccountDataRef.current()
          if (cancelled || refreshedAccountData?.isOrgAccount) return
        }

        console.error('Account bootstrap failed:', error)
        setBootstrapState({ status: 'error', uid })
      })

    return () => {
      cancelled = true
    }
  }, [uid, shouldSkipBootstrap, hasUserAccountData, surveyCompleted, retryCount])

  if (shouldSkipBootstrap) return children

  if (!uid || !hasUserAccountData) return <FullPageLoader />

  if (surveyCompleted) return children

  if (bootstrapState.uid === uid && bootstrapState.status === 'success') {
    return children
  }

  if (bootstrapState.uid === uid && bootstrapState.status === 'error') {
    return (
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center p-6">
        <ErrorCard
          actionLabel="Retry"
          code="500"
          message="We were unable to finish setting up your account. Please try again."
          onClick={() => setRetryCount((count) => count + 1)}
          title="Account Setup Error"
        />
      </div>
    )
  }

  return <FullPageLoader />
}
